import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../Markets.css';

const Commodity: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    }
  };

  const commodities = {
    metals: [
      { name: 'Gold (XAU/USD)', description: 'Safe-haven asset and inflation hedge' },
      { name: 'Silver (XAG/USD)', description: 'Industrial and precious metal' },
      { name: 'Platinum', description: 'Rare precious metal with industrial uses' },
      { name: 'Palladium', description: 'Key component in automotive catalysts' },
      { name: 'Copper', description: 'Industrial metal, economic health indicator' }
    ],
    energy: [
      { name: 'Crude Oil (WTI)', description: 'West Texas Intermediate, US benchmark' },
      { name: 'Brent Crude', description: 'International crude oil benchmark' },
      { name: 'Natural Gas', description: 'Major energy commodity for heating and power' },
      { name: 'Heating Oil', description: 'Refined petroleum product' },
      { name: 'Gasoline', description: 'Transportation fuel commodity' }
    ],
    agricultural: [
      { name: 'Wheat', description: 'Staple grain commodity' },
      { name: 'Corn', description: 'Major agricultural commodity' },
      { name: 'Soybeans', description: 'Key oilseed and protein source' },
      { name: 'Coffee', description: 'Global soft commodity' },
      { name: 'Sugar', description: 'Sweetener commodity' },
      { name: 'Cotton', description: 'Textile fiber commodity' }
    ]
  };

  return (
    <motion.div 
      className="markets-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="page-content">
        <motion.div className="page-title" variants={itemVariants}>
          <h1>Commodity Trading</h1>
          <p>Trade precious metals, energies, and agricultural commodities</p>
        </motion.div>

        

        <motion.section className="market-overview" variants={itemVariants}>
          <h2>What is Commodity Trading?</h2>
          <p>
            Commodity trading involves buying and selling raw materials or primary products that are either grown, extracted, or produced. 
            Commodities are the building blocks of the global economy and are traded on specialized exchanges worldwide.
          </p>
          <p>
            Unlike stocks or bonds, commodities are tangible assets with intrinsic value. They serve as hedges against inflation, 
            portfolio diversification tools, and opportunities to profit from global supply and demand dynamics.
          </p>
        </motion.section>

        <motion.section className="market-features" variants={itemVariants}>
          <h2>Why Trade Commodities?</h2>
          <div className="features-grid">
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Inflation Hedge</h3>
              <p>Commodities often rise in value during inflationary periods</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Portfolio Diversification</h3>
              <p>Low correlation with stocks and bonds enhances portfolio balance</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>High Volatility</h3>
              <p>Price swings create trading opportunities for active traders</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Global Demand</h3>
              <p>Essential materials with worldwide consumption and production</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Leverage Trading</h3>
              <p>Trade larger positions through CFDs and futures contracts</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>24-Hour Markets</h3>
              <p>Many commodities trade nearly 24 hours across global exchanges</p>
            </motion.div>
          </div>
        </motion.section>

        <motion.section className="commodities-categories" variants={itemVariants}>
          <h2>Precious Metals</h2>
          <p>Safe-haven assets and stores of value used in jewelry, industry, and investment</p>
          <div className="commodity-list">
            {commodities.metals.map((item, i) => (
              <motion.div key={i} className="commodity-item" variants={itemVariants} whileHover={{ scale: 1.03 }}>
                <h3>🥇 {item.name}</h3>
                <p>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="commodities-categories" variants={itemVariants}>
          <h2>Energy Commodities</h2>
          <p>Essential fuels powering transportation, heating, and industrial processes</p>
          <div className="commodity-list">
            {commodities.energy.map((item, i) => (
              <motion.div key={i} className="commodity-item" variants={itemVariants} whileHover={{ scale: 1.03 }}>
                <h3>⚡ {item.name}</h3>
                <p>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="commodities-categories" variants={itemVariants}>
          <h2>Agricultural Commodities</h2>
          <p>Food, fiber, and feed products essential for global consumption</p>
          <div className="commodity-list">
            {commodities.agricultural.map((item, i) => (
              <motion.div key={i} className="commodity-item" variants={itemVariants} whileHover={{ scale: 1.03 }}>
                <h3>🌾 {item.name}</h3>
                <p>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="market-factors" variants={itemVariants}>
          <h2>Factors Affecting Commodity Prices</h2>
          <div className="factors-grid">
            <div className="factor">
              <h4>📊 Supply & Demand</h4>
              <p>Production levels, inventory data, and consumption patterns</p>
            </div>
            <div className="factor">
              <h4>🌦️ Weather Conditions</h4>
              <p>Droughts, floods, and climate affecting agricultural production</p>
            </div>
            <div className="factor">
              <h4>💱 US Dollar Strength</h4>
              <p>Inverse relationship with dollar-denominated commodities</p>
            </div>
            <div className="factor">
              <h4>🌍 Geopolitical Events</h4>
              <p>Conflicts, sanctions, and trade policies affecting supply</p>
            </div>
            <div className="factor">
              <h4>📈 Economic Growth</h4>
              <p>Industrial demand correlates with economic expansion</p>
            </div>
            <div className="factor">
              <h4>🏭 Production Costs</h4>
              <p>Mining, extraction, and farming costs affecting profitability</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="trading-strategies" variants={itemVariants}>
          <h2>Commodity Trading Strategies</h2>
          <div className="strategies-grid">
            <div className="strategy">
              <h3>📈 Trend Following</h3>
              <p>Ride long-term trends driven by supply-demand fundamentals</p>
            </div>
            <div className="strategy">
              <h3>📰 Event-Based Trading</h3>
              <p>Trade around inventory reports, weather events, and OPEC meetings</p>
            </div>
            <div className="strategy">
              <h3>🔄 Spread Trading</h3>
              <p>Trade the difference between related commodities or contracts</p>
            </div>
            <div className="strategy">
              <h3>🛡️ Hedging</h3>
              <p>Use commodities to protect against inflation or currency risk</p>
            </div>
            <div className="strategy">
              <h3>📊 Seasonal Trading</h3>
              <p>Exploit recurring seasonal patterns in agricultural commodities</p>
            </div>
            <div className="strategy">
              <h3>💹 Correlation Trading</h3>
              <p>Trade based on relationships between commodities and currencies</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="trading-tips" variants={itemVariants}>
          <h2>Commodity Trading Tips</h2>
          <ul className="tips-list">
            <li>Monitor weekly inventory reports (EIA for oil, USDA for agriculture)</li>
            <li>Understand seasonal patterns and production cycles</li>
            <li>Watch the US Dollar Index as it inversely affects commodity prices</li>
            <li>Stay informed about major producing countries and geopolitical risks</li>
            <li>Use proper position sizing due to high volatility</li>
            <li>Consider storage costs and contract expiration dates when trading futures</li>
            <li>Follow central bank policies as they impact inflation expectations</li>
          </ul>
        </motion.section>

        <motion.div className="cta-section" variants={itemVariants}>
          <h2>Start Trading Commodities</h2>
          <p>Get professional signals and analysis for precious metals, energy, and agricultural markets</p>
          <div className="btn-container">
            <Link to="/services/signals" className="btn">Get Trading Signals</Link>
            <Link to="/education" className="btn secondary">Learn More</Link>
            <Link to="/ib-partners" className="btn secondary">Choose Your Broker</Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Commodity;
